import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Modal,
  MenuItem,
  Select,
  Paper,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

const EditModule = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [courseId, setCourseId] = useState('');
  const [module, setModule] = useState('');
  const [courseName, setCourseName] = useState('');
  const navigate = useNavigate();
  const { ModuleId } = useParams();

  // Static course data
  const courseData = [
    { courseId: 1, courseName: 'C++' },
    { courseId: 2, courseName: 'Web Development' },
    { courseId: 3, courseName: 'Java' },
    { courseId: 4, courseName: 'Python' },
    { courseId: 5, courseName: 'Data Structures' },
  ];

  // Static modules data
  const staticModules = [
    { ModuleId: 1, CourseName: 'C++', ModuleName: 'Array' },
    { ModuleId: 2, CourseName: 'Web Development', ModuleName: 'React' },
    { ModuleId: 3, CourseName: 'C++', ModuleName: 'Pointers' },
    { ModuleId: 4, CourseName: 'C++', ModuleName: 'OOP Concepts' },
    { ModuleId: 5, CourseName: 'Web Development', ModuleName: 'HTML & CSS' },
    { ModuleId: 6, CourseName: 'Web Development', ModuleName: 'JavaScript' },
    { ModuleId: 7, CourseName: 'Web Development', ModuleName: 'Node.js' },
    { ModuleId: 8, CourseName: 'Python', ModuleName: 'Functions' },
    { ModuleId: 9, CourseName: 'Python', ModuleName: 'Dictionaries' },
    { ModuleId: 10, CourseName: 'Java', ModuleName: 'Inheritance' },
    { ModuleId: 11, CourseName: 'Java', ModuleName: 'Collections Framework' },
    { ModuleId: 12, CourseName: 'Data Structures', ModuleName: 'Linked List' },
    { ModuleId: 13, CourseName: 'Data Structures', ModuleName: 'Stacks & Queues' },
    { ModuleId: 14, CourseName: 'Web Development', ModuleName: 'MongoDB' },
  ];

  // Find the module to edit based on ModuleId
  const moduleToEdit = staticModules.find(mod => mod.ModuleId === parseInt(ModuleId));

  useEffect(() => {
    if (moduleToEdit) {
      // Find the courseId based on CourseName
      const course = courseData.find(c => c.courseName === moduleToEdit.CourseName);
      if (course) {
        setCourseId(course.courseId);
        setCourseName(course.courseName);
      }
      setModule(moduleToEdit.ModuleName);
    }
  }, [moduleToEdit]);

  const handleSave = (e) => {
    e.preventDefault();
    // Simulate API call with timeout
    setTimeout(() => {
      console.log('module updated:', { moduleName: module, courseId });
      setShowSuccessModal(true);
    }, 500);
  };

  const handleCancel = () => {
   navigate(-1);
  };

  if (!moduleToEdit) {
    return (
      <Container sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h5">Module not found</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }} variant="contained">
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '50vh',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'rgba(2, 78, 101, 1)', // dark navy blue
          padding: '12px 16px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          width: "100%",
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Edit Module
        </Typography>
      </Box>

      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6, backgroundColor: '#f8fdfeff' }}>
        <div>
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel htmlFor="course">Course</InputLabel>
            <Select
              id="course"
              value={courseId}
              label="course"
              onChange={(e) => setCourseId(e.target.value)}
            >
              {courseData.map((courseItem) => (
                <MenuItem key={courseItem.courseId} value={courseItem.courseId}>
                  {courseItem.courseName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            id="module"
            label="Module"
            variant="outlined"
            value={module}
            onChange={(e) => setModule(e.target.value)}
            sx={{ mb: 3 }}
          />
          <Tooltip title={!courseId ? "Select course" : !module ? "Enter module Name" : ""} arrow>
            <span>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  mr: 2,
                  backgroundColor: '#127498ff',
                  '&:hover': { backgroundColor: '#116280ff' }
                }}
                disabled={!module || !courseId}
              >
                UPDATE
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
          >
            CANCEL
          </Button>
        </div>
      </Paper>

      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Module Updated ✅
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Module Updated Successfully
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1); // go back after closing modal
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default EditModule;